import React, { useState } from 'react'
import { Panel, Button, Table, ButtonToolbar, IconButton, Tag, toaster, Message } from 'rsuite'
import { formatDistanceToNow } from 'date-fns'
import { ru } from 'date-fns/locale'
import PlusIcon from '@rsuite/icons/Plus'
import EditIcon from '@rsuite/icons/Edit'
import TrashIcon from '@rsuite/icons/Trash'
import { generatePath, useHistory } from 'react-router-dom'
import { usePartner } from '@@partner:helpers/hooks/auth'
import { ROUTE_MARKETING_LOYALTY_COUPON_ADD, ROUTE_MARKETING_LOYALTY_COUPON_EDIT } from '@@partner:config/routes'
import CommonErrorDisplay from '@components/common/CommonErrorDisplay'
import { useFetchAllLoyaltyCouponSeries } from '@@partner:graphql/query/loyaltyCouponSeries/allLoyaltyCouponSeries.gql'
import { useMutateDeleteLoyaltyCouponSeries } from '@@partner:graphql/mutation/loyaltyCouponSeries/saveLoyaltyCouponSeries.gql'
import type { LoyaltyCouponSeries } from '@@partner:types:app'
import type { GQL_ID } from '@@partner:types:graphql'

const { Column, HeaderCell, Cell } = Table

export default function LoyaltyCouponsGrid () {
  const { brandSlug } = usePartner()
  const history = useHistory()
  
  const { data, loading, error, refetch } = useFetchAllLoyaltyCouponSeries()
  const [ deleteLoyaltyCouponSeries ] = useMutateDeleteLoyaltyCouponSeries()

  const handleAdd = () => {
    const path = generatePath(ROUTE_MARKETING_LOYALTY_COUPON_ADD, { brandSlug })
    history.push(path)
  }

  const handleEdit = (id: GQL_ID) => {
    const path = generatePath(ROUTE_MARKETING_LOYALTY_COUPON_EDIT, { brandSlug, couponSeriesId: id.toString() })
    history.push(path)
  }

  const handleDelete = async (id: GQL_ID) => {
    try {
      await deleteLoyaltyCouponSeries({ variables: { id } })
      toaster.push(
        <Message type="success">
          Серия купонов успешно удалена
        </Message>
      )
      refetch()
    }
    catch (error) {
      toaster.push(
        <Message type="error">
          Ошибка при удалении серии купонов
        </Message>
      )
    }
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return '—'
    try {
      return formatDistanceToNow(new Date(dateString), { 
        addSuffix: true, 
        locale: ru 
      })
    }
    catch {
      return '—'
    }
  }

  const StatusCell = ({ rowData }: { rowData: LoyaltyCouponSeries }) => (
    <Tag color={rowData.isActive ? 'green' : 'red'}>
      {rowData.isActive ? 'Активна' : 'Неактивна'}
    </Tag>
  )

  const ActionsCell = ({ rowData }: { rowData: LoyaltyCouponSeries }) => (
    <ButtonToolbar>
      <IconButton
        icon={<EditIcon />}
        size="sm"
        onClick={() => handleEdit(rowData.id!)}
        title="Редактировать"
      />
      <IconButton
        icon={<TrashIcon />}
        size="sm"
        color="red"
        appearance="ghost"
        onClick={() => handleDelete(rowData.id!)}
        title="Удалить"
      />
    </ButtonToolbar>
  )

  if (error) {
    return <CommonErrorDisplay error={error} />
  }

  const series = data?.loyaltyCouponSeries?.edges || []

  return (
    <Panel
      header={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h4>Серии купонов лояльности</h4>
          <Button
            appearance="primary"
            startIcon={<PlusIcon />}
            onClick={handleAdd}
          >
            Добавить серию
          </Button>
        </div>
      }
    >
      <Table
        data={series}
        loading={loading}
        height={400}
        autoHeight
      >
        <Column width={120} fixed>
          <HeaderCell>Код</HeaderCell>
          <Cell dataKey="code" />
        </Column>

        <Column width={200} flexGrow={1}>
          <HeaderCell>Название</HeaderCell>
          <Cell dataKey="title" />
        </Column>

        <Column width={250} flexGrow={1}>
          <HeaderCell>Описание</HeaderCell>
          <Cell dataKey="description" />
        </Column>

        <Column width={120}>
          <HeaderCell>Действует с</HeaderCell>
          <Cell>
            {(rowData: LoyaltyCouponSeries) => formatDate(rowData.validFrom)}
          </Cell>
        </Column>

        <Column width={120}>
          <HeaderCell>Действует до</HeaderCell>
          <Cell>
            {(rowData: LoyaltyCouponSeries) => formatDate(rowData.validUntil)}
          </Cell>
        </Column>

        <Column width={100}>
          <HeaderCell>Статус</HeaderCell>
          <Cell>
            {(rowData: LoyaltyCouponSeries) => <StatusCell rowData={rowData} />}
          </Cell>
        </Column>

        <Column width={120}>
          <HeaderCell>Создана</HeaderCell>
          <Cell>
            {(rowData: LoyaltyCouponSeries) => formatDate(rowData.created_at)}
          </Cell>
        </Column>

        <Column width={120} fixed="right">
          <HeaderCell>Действия</HeaderCell>
          <Cell>
            {(rowData: LoyaltyCouponSeries) => <ActionsCell rowData={rowData} />}
          </Cell>
        </Column>
      </Table>
    </Panel>
  )
}
